import { useCallback, useEffect, useRef } from 'react';

import { usePostHogAnalytics } from './usePostHog/usePostHogAnalytics.hook';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { type SupportAiTriggerId, useSupportWidgetStore } from '@/Domain/store/supportWidget.store';

// Long enough not to read as a side effect of pressing Enter, short enough to still land in view.
export const SUPPORT_AI_AUTO_OPEN_DELAY_MS = 1500;

export type SupportAiTriggerRequest = {
  trigger: SupportAiTriggerId;
  /** Sent to Support AI verbatim. Callers cap and sanitise it. */
  message: string;
  /** One fire per key per page load; defaults to the trigger id. */
  dedupeKey?: string;
  /** Merged into support_ai_auto_opened / _suppressed. */
  properties?: Record<string, unknown>;
};

/** The one way to open Support AI on the user's behalf; owns the delay, guards and analytics. */
export const useSupportAiTrigger = () => {
  const { capture } = usePostHogAnalytics();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  const triggerSupportAi = useCallback(
    ({ trigger, message, dedupeKey, properties }: SupportAiTriggerRequest) => {
      if (timerRef.current !== null) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const outcome = useSupportWidgetStore
          .getState()
          .autoOpenWithMessage(dedupeKey ?? trigger, message, trigger);
        capture(
          outcome === 'opened'
            ? PostHogEventEnum.SupportAiAutoOpened
            : PostHogEventEnum.SupportAiAutoOpenSuppressed,
          outcome === 'opened'
            ? { trigger, ...properties }
            : { trigger, reason: outcome, ...properties },
        );
      }, SUPPORT_AI_AUTO_OPEN_DELAY_MS);
    },
    [capture],
  );

  // The delay outlives the send, so a caller whose surface goes away has to be able to call it off.
  const cancelSupportAiTrigger = useCallback(() => {
    if (timerRef.current === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  return { triggerSupportAi, cancelSupportAiTrigger };
};
